import {
  DEFAULT_PLAN_DEFINITIONS,
  type PlanDefinition,
  type PlanFeatureFlag,
  type PlanId,
} from '../constants/planDefinitions';

export type QuotaKind = 'product' | 'classified' | 'property';

const PLAN_RANK: Record<PlanId, number> = {
  starter: 0,
  growth: 1,
  pro: 2,
};

/** Maps trimmed, case-insensitive plan strings to canonical PlanId. Returns null if unknown. */
export function normalizePlanIdAlias(raw: string): PlanId | null {
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  const ALIASES: Record<string, PlanId> = {
    starter: 'starter',
    free: 'starter',
    basic: 'starter',
    starter_plan: 'starter',
    growth: 'growth',
    growth_plan: 'growth',
    growthplan: 'growth',
    pro: 'pro',
    professional: 'pro',
    pro_plan: 'pro',
    proplan: 'pro',
  };
  return ALIASES[key] ?? null;
}

export type VendorPlanResolution =
  | { state: 'loading' }
  | { state: 'resolved'; planId: PlanId; raw?: string }
  | { state: 'unverified'; raw: string };

/**
 * Three-state vendor plan resolution for UI gating.
 * - loading: vendor profile not hydrated yet
 * - resolved + starter: null/empty planId (never subscribed)
 * - unverified: non-empty value that does not normalize
 */
export function resolveVendorPlanId(
  subscriptionPlanId: string | undefined | null,
  vendorLoaded: boolean,
): VendorPlanResolution {
  if (!vendorLoaded) return { state: 'loading' };
  const raw = subscriptionPlanId?.trim();
  if (!raw) return { state: 'resolved', planId: 'starter' };
  const planId = normalizePlanIdAlias(raw);
  if (planId) return { state: 'resolved', planId, raw };
  return { state: 'unverified', raw };
}

/**
 * Legacy quota/backend helper — empty → starter; known alias → plan.
 * Unknown non-empty values log a warning and fall back to starter for safe quota math only.
 * UI gating must use resolveVendorPlanId instead.
 */
export function normalizePlanId(planId: string | undefined | null): PlanId {
  if (planId == null || String(planId).trim() === '') return 'starter';
  const resolved = normalizePlanIdAlias(String(planId));
  if (resolved) return resolved;
  if (typeof console !== 'undefined') {
    console.warn(
      '[ShopGND] Unknown subscriptionPlanId; using starter for quota fallback:',
      planId,
    );
  }
  return 'starter';
}

function normalizePlanRow(base: PlanDefinition, row: Partial<PlanDefinition>): PlanDefinition {
  const merged = { ...base, ...row, id: row.id ?? base.id };
  return {
    ...merged,
    features: Array.isArray(row.features) ? row.features : base.features,
    marketingFeatures: Array.isArray(row.marketingFeatures)
      ? row.marketingFeatures
      : (base.marketingFeatures ?? []),
    notIncluded: Array.isArray(row.notIncluded) ? row.notIncluded : (base.notIncluded ?? []),
  };
}

export function mergePlanDefinitions(overrides: PlanDefinition[] | undefined): PlanDefinition[] {
  if (!overrides?.length) return DEFAULT_PLAN_DEFINITIONS;
  const byId = new Map<PlanId, PlanDefinition>();
  for (const base of DEFAULT_PLAN_DEFINITIONS) byId.set(base.id, { ...base });
  for (const row of overrides) {
    if (row?.id && byId.has(row.id)) {
      byId.set(row.id, normalizePlanRow(byId.get(row.id)!, row));
    }
  }
  return [...byId.values()];
}

export function getPlanDefinition(
  planId: PlanId | string | undefined | null,
  definitions: PlanDefinition[] = DEFAULT_PLAN_DEFINITIONS,
): PlanDefinition {
  const id = normalizePlanId(planId ?? undefined);
  const list = definitions.length > 0 ? definitions : DEFAULT_PLAN_DEFINITIONS;
  const found = list.find((p) => p.id === id) ?? list[0] ?? DEFAULT_PLAN_DEFINITIONS[0];
  return {
    ...found,
    features: found.features ?? [],
    marketingFeatures: found.marketingFeatures ?? [],
    notIncluded: found.notIncluded ?? [],
  };
}

export function quotaForPlan(
  planId: PlanId | string | undefined | null,
  kind: QuotaKind,
  definitions: PlanDefinition[] = DEFAULT_PLAN_DEFINITIONS,
): number | null {
  const plan = getPlanDefinition(planId, definitions);
  if (kind === 'product') return plan.maxProductListings;
  if (kind === 'classified') return plan.maxClassifiedListings;
  return plan.maxPropertyListings;
}

export function atQuotaLimit(
  planId: PlanId | string | undefined | null,
  kind: QuotaKind,
  currentCount: number,
  definitions: PlanDefinition[] = DEFAULT_PLAN_DEFINITIONS,
): boolean {
  const max = quotaForPlan(planId, kind, definitions);
  if (max === null) return false;
  return currentCount >= max;
}

export function planHasFeature(
  planId: PlanId | string | undefined | null,
  feature: PlanFeatureFlag | 'property_listings',
  definitions: PlanDefinition[] = DEFAULT_PLAN_DEFINITIONS,
): boolean {
  const plan = getPlanDefinition(planId, definitions);
  if (feature === 'property_listings') {
    return plan.maxPropertyListings === null || plan.maxPropertyListings > 0;
  }
  const features = plan.features ?? [];
  if (feature === 'classifieds') {
    return (
      features.includes('classifieds') ||
      plan.maxClassifiedListings === null ||
      plan.maxClassifiedListings > 0
    );
  }
  return features.includes(feature);
}

export function planMeetsMinimum(current: PlanId, required: PlanId): boolean {
  return PLAN_RANK[current] >= PLAN_RANK[required];
}

export function quotaLabel(max: number | null, noun: string): string {
  if (max === null) return `Unlimited ${noun}`;
  if (max === 0) return `No ${noun}`;
  return `Up to ${max} ${noun}`;
}
